import React from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Search, Menu, X } from "lucide-react";

const NAV = [
  { to: "/read", label: "Read" },
  { to: "/watch", label: "Watch" },
  { to: "/listen", label: "Listen" },
  { to: "/use", label: "Use" },
  { to: "/topics", label: "Topics" },
  { to: "/contribute", label: "Contribute" },
  { to: "/about", label: "About" },
];

function Header() {
  const [open, setOpen] = React.useState(false);
  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-oc-beige/95 backdrop-blur-md border-b border-oc-hairline"
    >
      <div className="max-w-editorial mx-auto px-6 md:px-10 flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2.5 group">
          <img
            src="/oc-mark.png"
            alt=""
            aria-hidden="true"
            className="w-7 h-7 md:w-8 md:h-8 shrink-0 transition-transform group-hover:-rotate-3"
          />
          <span className="wordmark text-lg md:text-xl">
            Operating<span className="accent">/</span>Class
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-ui text-sm font-semibold tracking-wide transition-colors ${
                  isActive ? "text-oc-blue" : "text-oc-ink hover:text-oc-blue"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/search"
            data-testid="header-search"
            aria-label="Search"
            className="hidden md:inline-flex items-center justify-center w-9 h-9 border border-oc-hairline hover:border-oc-ink transition-colors"
          >
            <Search className="w-4 h-4" />
          </Link>
          <Link
            to="/nominate"
            data-testid="header-nominate"
            className="hidden md:inline-flex font-ui text-xs font-bold uppercase tracking-widest border border-oc-ink px-4 h-9 items-center hover:bg-oc-ink hover:text-white transition-colors"
          >
            Nominate
          </Link>
          <Link
            to="/contribute"
            data-testid="header-contribute"
            className="hidden md:inline-flex font-ui text-xs font-bold uppercase tracking-widest bg-oc-blue text-white px-4 h-9 items-center hover:bg-oc-blue-hover transition-colors"
          >
            Contribute
          </Link>
          <button
            data-testid="header-menu"
            className="lg:hidden inline-flex items-center justify-center w-9 h-9 border border-oc-hairline"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-oc-hairline bg-oc-beige px-6 py-6 space-y-4">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              className="block font-ui text-base font-semibold text-oc-ink"
            >
              {n.label}
            </Link>
          ))}
          <div className="pt-4 flex gap-3">
            <Link
              to="/contribute"
              onClick={() => setOpen(false)}
              className="flex-1 font-ui text-xs font-bold uppercase tracking-widest bg-oc-blue text-white px-4 h-11 flex items-center justify-center"
            >
              Contribute
            </Link>
            <Link
              to="/nominate"
              onClick={() => setOpen(false)}
              className="flex-1 font-ui text-xs font-bold uppercase tracking-widest border border-oc-ink px-4 h-11 flex items-center justify-center"
            >
              Nominate
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-oc-ink text-white mt-24">
      <div className="max-w-editorial mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Link to="/" className="wordmark text-2xl text-white inline-flex items-center gap-3">
              <img src="/oc-mark.png" alt="" aria-hidden="true" className="w-8 h-8" />
              <span>Operating<span className="text-oc-coral">/</span>Class</span>
            </Link>
            <p className="mt-6 font-editorial text-lg text-white/70 leading-relaxed max-w-md">
              {"Business lessons from the people actually operating businesses. Edited by "}
              <a
                href="https://quentinyoung.ca"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-quentin-link"
                className="underline underline-offset-4 decoration-oc-coral/60 hover:decoration-oc-coral text-white"
              >
                Quentin Young
              </a>.
            </p>
            <div className="mt-8 flex gap-3">
              <Link
                to="/contribute"
                className="font-ui text-xs font-bold uppercase tracking-widest bg-oc-coral text-white px-5 h-11 flex items-center hover:bg-oc-coral-hover transition-colors"
              >
                Contribute
              </Link>
              <Link
                to="/nominate"
                className="font-ui text-xs font-bold uppercase tracking-widest border border-white/40 text-white px-5 h-11 flex items-center hover:border-white transition-colors"
              >
                Nominate
              </Link>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="eyebrow text-white/50 mb-4">Formats</div>
            <ul className="space-y-3 font-ui text-sm">
              <li><Link to="/read" className="hover:text-oc-coral">Read</Link></li>
              <li><Link to="/watch" className="hover:text-oc-coral">Watch</Link></li>
              <li><Link to="/listen" className="hover:text-oc-coral">Listen</Link></li>
              <li><Link to="/use" className="hover:text-oc-coral">Use</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="eyebrow text-white/50 mb-4">Browse</div>
            <ul className="space-y-3 font-ui text-sm">
              <li><Link to="/topics" className="hover:text-oc-coral">Topics</Link></li>
              <li><Link to="/industries" className="hover:text-oc-coral">Industries</Link></li>
              <li><Link to="/operators" className="hover:text-oc-coral">Contributors</Link></li>
              <li><Link to="/collections" className="hover:text-oc-coral">Collections</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="eyebrow text-white/50 mb-4">Newsletter</div>
            <p className="font-editorial text-sm text-white/70 mb-4">
              New interviews, operator insights, curated collections, delivered occasionally, never noisily.
            </p>
            <Link
              to="/newsletter"
              className="font-ui text-xs font-bold uppercase tracking-widest border-b border-oc-coral text-white hover:text-oc-coral pb-1 transition-colors"
            >
              Subscribe →
            </Link>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="font-ui text-xs text-white/50">
            © {new Date().getFullYear()}
            {" Operating Class. Edited by "}
            <a
              href="https://quentinyoung.ca"
              target="_blank"
              rel="noreferrer"
              className="hover:text-white underline underline-offset-2 decoration-white/20"
            >
              Quentin Young
            </a>.
          </p>
          <div className="flex flex-wrap gap-6 font-ui text-xs text-white/50 items-center">
            <Link to="/about" className="hover:text-white">Editorial standards</Link>
            <Link to="/manifesto" className="hover:text-white">Manifesto</Link>
            <Link to="/newsletter" className="hover:text-white">Newsletter</Link>
            <Link to="/partner" className="hover:text-white">Partner</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
            <a
              href="https://kostaja.ca"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-kostaja-credit"
              className="hover:text-white"
            >
              Web design by <span className="text-white/80">Kostaja</span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function SiteLayout() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return (
    <div className="min-h-screen bg-oc-beige text-oc-ink flex flex-col">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
